import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Zap } from "lucide-react";
import { getMyCredits } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useFeatureFlags } from "../lib/feature_flags";

/**
 * Compact credit-balance pill rendered in TopNav.
 *
 * Polls `/me/credits` on mount + every 90s, and re-fetches when the
 * paywall closes (so a fresh subscription shows up immediately). Click
 * opens `/subscriptions` so out-of-credit users can upgrade fast.
 */
export default function CreditBalancePill() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [balance, setBalance] = useState(null);
    const isSuperadmin = (user?.role || "") === "superadmin";
    const flags = useFeatureFlags();
    const showSubscriptions = flags.show_subscriptions !== false;

    const refresh = useCallback(async () => {
        if (!user || isSuperadmin) return;
        try {
            const b = await getMyCredits();
            setBalance(b);
        } catch {
            /* silent — fresh logins may not have a sub yet */
        }
    }, [user, isSuperadmin]);

    useEffect(() => {
        refresh();
        const id = setInterval(refresh, 90 * 1000);
        const onFocus = () => refresh();
        window.addEventListener("focus", onFocus);
        const onPaywallClose = () => refresh();
        window.addEventListener("paywall:close", onPaywallClose);
        return () => {
            clearInterval(id);
            window.removeEventListener("focus", onFocus);
            window.removeEventListener("paywall:close", onPaywallClose);
        };
    }, [refresh]);

    if (isSuperadmin || !user) return null;
    // iter-108: hide entirely when the superadmin has flipped the
    // Subscriptions feature flag off (internal / credit-free deploys).
    if (!showSubscriptions) return null;
    if (!balance) return null;

    const remaining = Number(balance.credits_remaining || 0);
    const total = Number(balance.credit_amount || 0);
    const low = balance.has_subscription && total > 0 && remaining / total < 0.15;
    const empty = !balance.has_subscription || remaining <= 0;

    return (
        <button
            type="button"
            onClick={() => navigate("/subscriptions")}
            data-testid="credit-balance-pill"
            title={
                balance.has_subscription
                    ? `${balance.tier_title} — ${remaining.toLocaleString()} / ${total.toLocaleString()} credits left`
                    : "No active subscription — click to view plans"
            }
            className={`hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-bold tabular-nums transition-colors ${
                empty
                    ? "bg-rose-500/10 border-rose-500/40 text-rose-300 hover:bg-rose-500/20"
                    : low
                      ? "bg-amber-500/10 border-amber-500/40 text-amber-300 hover:bg-amber-500/20"
                      : "bg-[#1C253C] border-[#2A3655] text-slate-200 hover:border-[#00A3FF]/60 hover:text-white"
            }`}
        >
            {empty ? (
                <Zap className="w-3 h-3" />
            ) : (
                <Sparkles className="w-3 h-3" />
            )}
            <span data-testid="credit-balance-amount">
                {empty
                    ? "Subscribe"
                    : `${remaining.toLocaleString()} cr`}
            </span>
        </button>
    );
}
